import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const OfflineIndicator = ({ companyOptions, pendingEntries, onSync }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const getCompanyName = (companyValue) => {
    const company = companyOptions.find((c) => c.value === companyValue);

    return company ? company.label : companyValue;
  };

  // Load last sync time from localStorage
  useEffect(() => {
    const savedSyncTime = localStorage.getItem("lastSyncTime");
    if (savedSyncTime) {
      setLastSyncTime(new Date(savedSyncTime));
    }
  }, []);

  const handleSync = async () => {
    if (!isOnline || pendingEntries.length === 0) return;

    setIsSyncing(true);

    try {
      // Simulate sync process
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Call parent sync handler
      await onSync();

      // Update last sync time
      const now = new Date();
      setLastSyncTime(now);
      localStorage.setItem("lastSyncTime", now.toISOString());
    } catch (error) {
      console.error("Sync error:", error);
    } finally {
      setIsSyncing(false);
    }
  };

  const formatLastSync = (date) => {
    if (!date) return "Never";

    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getStatusConfig = () => {
    if (!isOnline) {
      return {
        icon: "WifiOff",
        color: "text-error",
        bgColor: "bg-error/10",
        label: "Offline",
        description: "Data will sync when connection is restored",
      };
    }

    if (pendingEntries.length > 0) {
      return {
        icon: "CloudOff",
        color: "text-warning",
        bgColor: "bg-warning/10",
        label: "Sync Pending",
        description: `${pendingEntries.length} entries waiting to sync`,
      };
    }

    return {
      icon: "Wifi",
      color: "text-success",
      bgColor: "bg-success/10",
      label: "Online",
      description: "All data synchronized",
    };
  };

  const statusConfig = getStatusConfig();

  // Don't show if online and no pending entries
  if (isOnline && pendingEntries.length === 0) {
    return null;
  }

  return (
    <div
      className={`rounded-lg border p-4 ${statusConfig.bgColor} border-current/20`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full ${statusConfig.bgColor}`}
          >
            <Icon
              name={statusConfig.icon}
              size={16}
              className={statusConfig.color}
            />
          </div>
          <div>
            <div className={`text-sm font-medium ${statusConfig.color}`}>
              {statusConfig.label}
            </div>
            <div className="text-xs text-muted-foreground">
              {statusConfig.description}
            </div>
            {lastSyncTime && (
              <div className="text-xs text-muted-foreground mt-1">
                Last sync: {formatLastSync(lastSyncTime)}
              </div>
            )}
          </div>
        </div>

        {isOnline && pendingEntries.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleSync}
            loading={isSyncing}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Sync Now
          </Button>
        )}
      </div>

      {/* Pending Entries Preview */}
      {pendingEntries.length > 0 && (
        <div className="mt-4 pt-4 border-t border-current/20">
          <div className="text-xs font-medium text-muted-foreground mb-2">
            Pending Entries ({pendingEntries.length})
          </div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {pendingEntries.slice(0, 3).map((entry, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-xs"
              >
                <span className="text-foreground truncate">
                  {getCompanyName(entry.company_id)} -{" "}
                  {new Date(entry.entry_date).toLocaleDateString()}
                </span>
                <span className="text-muted-foreground ml-2">
                  ${entry.salesAmount?.toLocaleString()}
                </span>
              </div>
            ))}
            {pendingEntries.length > 3 && (
              <div className="text-xs text-muted-foreground text-center pt-1">
                +{pendingEntries.length - 3} more entries
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflineIndicator;
